/**
 * Account settings sync (Phase 8): after sign-in the server's copy of the device-independent
 * settings (bindings, gamepad, audio, accessibility) replaces the local one; an account with no
 * copy yet is seeded from this device; later local changes reach the server after a short
 * debounce. Offline or signed out, the local settings simply stay local.
 */
import { UserSettingsSchema, type UserSettings } from '@steelwake/shared/progression';

import { api } from '../api.js';
import { useAppStore } from '../store.js';
import { userSettingsOf, withUserSettings } from './schema.js';
import { useSettingsStore } from './store.js';

const DEBOUNCE_MS = 800;
const PATH = '/api/v1/profile/settings';

let installed = false;
let applyingRemote = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastSent = '';
let pending: Promise<void> | null = null;

async function push(settings: UserSettings): Promise<void> {
  const body = JSON.stringify(settings);
  lastSent = body;
  try {
    await api(PATH, { method: 'PUT', body: settings });
  } catch {
    // Offline or signed out meanwhile: the next change tries again.
    lastSent = '';
  }
}

async function pull(): Promise<void> {
  try {
    const res = await api<{ settings: unknown }>(PATH);
    const parsed = UserSettingsSchema.safeParse(res.settings);
    const local = useSettingsStore.getState().settings;
    if (!parsed.success) {
      // First sign-in on this account: this device seeds the server.
      await push(userSettingsOf(local));
      return;
    }
    applyingRemote = true;
    try {
      useSettingsStore.getState().replace(withUserSettings(local, parsed.data));
    } finally {
      applyingRemote = false;
    }
    lastSent = JSON.stringify(parsed.data);
  } catch {
    // The API is unreachable: local settings stay in force.
  }
}

function schedule(settings: UserSettings): void {
  if (JSON.stringify(settings) === lastSent) return;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    pending = push(settings).finally(() => {
      pending = null;
    });
  }, DEBOUNCE_MS);
}

/** Starts the sync (idempotent). */
export function installSettingsSync(): void {
  if (installed) return;
  installed = true;
  useAppStore.subscribe((state, previous) => {
    if (state.authStatus === 'authenticated' && previous.authStatus !== 'authenticated') {
      void pull();
    }
  });
  if (useAppStore.getState().authStatus === 'authenticated') void pull();
  useSettingsStore.subscribe((state) => {
    if (applyingRemote || useAppStore.getState().authStatus !== 'authenticated') return;
    schedule(userSettingsOf(state.settings));
  });
}

/** Sends any change waiting on the debounce now (tests, sign-out). */
export async function flushSettingsSync(): Promise<void> {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
    await push(userSettingsOf(useSettingsStore.getState().settings));
  }
  if (pending) await pending;
}
